/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import NProgress from 'nprogress';
import Backbone from 'backbone';
import _ from 'lodash';

import Scaffold from '@/Scaffold';
import * as NavUtils from '@/util/NavUtils';
import { getUserDetails, isAuthorized, userHasRole } from '@/util/RoleUtils';
import ErrorView from '@/views/ErrorView';

import OrderPage from '../pages/OrderPage';
import GetAllLUOrderLineItems from '../services/GetAllLUOrderLineItems';
import GetCustomerOrderById from '../services/GetCustomerOrderById';
import GetCustomerOrderDetails from '../services/GetCustomerOrderDetails';
import GetOrderPackage from '../../order-package/services/GetOrderPackage';
import GetCommandPOCByOrderId from '../services/GetCommandPOCByOrderId';
import GetAWPackage from '../../../src/aw-package/services/GetAWPackage'

export default async function ShowOrderPage(orderId, activeSectionId, readonly = true) {
  NProgress.start();

  try {
    if (!isAuthorized() && EXTERNAL) {
      showAuthError();
      return;
    }

    const [allLineItems, orderDetails, extOrderDetails, commandPOCs] = await Promise.all([
      GetAllLUOrderLineItems(),
      GetCustomerOrderById(orderId),
      GetCustomerOrderDetails(orderId),
      GetCommandPOCByOrderId(orderId)
    ]);

    const isCRSOwner = getUserDetails().id === orderDetails?.crsOwner;
    const isEPCOwner = getUserDetails().id === orderDetails?.epcOwner;
    const isInternalUser = userHasRole('CRS') || userHasRole('EPC');
    const statusId = orderDetails.statusId;
    const isDraft = statusId === '1' ? true : false;
    let isInternalError;

    let awPackageStatusID;
    const orderPackageOwners = {};
    
    if (orderDetails.orderPackageId && !EXTERNAL) {
      const orderPackageDetails = await GetOrderPackage(orderDetails.orderPackageId);
      const awPackageDetails = await GetAWPackage(orderPackageDetails.awPackageID && orderPackageDetails.awPackageID);
      awPackageStatusID = awPackageDetails.statusId;
      orderPackageOwners.isCRSOwner = getUserDetails().id === orderPackageDetails?.crsInternalUserID;
      orderPackageOwners.isEPCOwner = getUserDetails().id === orderPackageDetails?.epcInternalUserID;
    }
   

    if (EXTERNAL) {
      NavUtils.navigate(`ext/order/${orderId}`);
    } else if (isInternalUser && !isDraft) {
      NavUtils.navigate(`order/${orderId}`);
    } else if (isInternalUser && isDraft) {
      isInternalError = true;
      showPermissionError(isInternalError);
      return;
    } else {
      isInternalError = false;
      showPermissionError(isInternalError);
      return;
    }

    Scaffold.getRegion('content').show(
      new OrderPage({
        activeSectionId,
        orderId,
        statusId,
        statusName: getStatusName(extOrderDetails),
        isCRSOwner,
        isEPCOwner,
        orderPackageOwners,
        model: new Backbone.Model({
          orderId,
          allLineItems,
          orderDetails,
          extOrderDetails,
          commandPOCs,
          awPackageStatusID,
        })
      })
    );
  } catch (err) {
    Scaffold.getRegion('content').show(
      new ErrorView({
        model: new Backbone.Model({
          errorMessage: _.isString(err.message) ? err.message : 'An Error Occurred',
          errorDetails: 'Something went wrong while loading the Order page.'
        })
      })
    );
  } finally {
    NProgress.done();
  }
}

function getStatusName(orderDetails) {
  return EXTERNAL ? orderDetails.externalOrderStatus : orderDetails.internalOrderStatus;
}

function showAuthError() {
  Scaffold.getRegion('content').show(
    new ErrorView({
      model: new Backbone.Model({
        errorMessage: 'Not Authorized',
        errorDetails: 'You are not yet an authorized user. Please contact an administrator.'
      })
    })
  );
  NProgress.done();
}

function showPermissionError(isInternalError) {
  let errorDetails;
  isInternalError
    ? (errorDetails = 'This order has not been submitted by the customer.')
    : (errorDetails = 'You are not allowed to view this order.');

  Scaffold.getRegion('content').show(
    new ErrorView({
      model: new Backbone.Model({
        errorMessage: 'An Error Occurred',
        errorDetails
      })
    })
  );
  NProgress.done();
}
